/**
 * Resolución de opciones válidas desde plantilla Abacus SOC.
 * La API expone `conditional_options` (reglas compuestas) e `inputs[].value_options` (fallback).
 */

function splitPipeList(value) {
  return String(value || '')
    .split('|')
    .map((s) => s.trim())
    .filter(Boolean)
}

function evalConditionalRule(rule, answers) {
  if (!rule || !rule.field_key) return false
  const fieldValue = answers[rule.field_key]
  if (fieldValue == null || fieldValue === '') return false

  const expected = String(rule.value)
  const actual = String(fieldValue)

  switch (rule.operator) {
    case 'eq':
      return actual === expected
    case 'in_list':
      return splitPipeList(expected).includes(actual)
    default:
      return false
  }
}

/** Valida `dependant_on` / `possible_values` del bloque (p. ej. tipo_de_financiamiento). */
function coincideDependantOnEntrada(entry, answers) {
  const dep = entry.dependant_on
  if (!dep) return true
  const depVal = answers[dep]
  if (depVal == null || depVal === '') return false
  const op = entry.operator || 'in_list'
  const valores = splitPipeList(entry.possible_values)
  if (op === 'eq') return String(depVal) === String(entry.possible_values)
  return valores.includes(String(depVal))
}

function coincideReglasCondicionales(entry, answers) {
  const rules = (entry.conditional && entry.conditional.conditional_rules) || []
  if (!rules.length) return false
  return rules.every((rule) => evalConditionalRule(rule, answers))
}

/** Bloque `conditional_options` válido: dependant_on + todas las conditional_rules. */
function coincideEntradaCondicional(entry, answers) {
  return coincideDependantOnEntrada(entry, answers) && coincideReglasCondicionales(entry, answers)
}

/**
 * Patrón Coppel para Mejora:
 * conditional_options.find(tasa_de_interes).options
 *   .find(rule => conditional_rules.some(r => r.value === "Mejora de hipoteca"))
 *   → .options
 */
export function findOpcionesTasaMejoraCoppel(template, answers) {
  const condBlock = (template && template.conditional_options || []).find((c) => c.key === 'tasa_de_interes')
  if (!condBlock) return []

  const entry = (condBlock.options || []).find((opt) => {
    const rules = (opt.conditional && opt.conditional.conditional_rules) || []
    return rules.some((r) => r.value === 'Mejora de hipoteca')
  })

  if (!entry || !coincideEntradaCondicional(entry, answers)) return []
  return normalizarOpcionesNumericas(entry.options || []).sort((a, b) => a - b)
}

function coincideValueOption(entry, answers) {
  const dep = entry.dependant_on
  if (!dep) return false
  const depVal = answers[dep]
  if (depVal == null || depVal === '') return false
  return splitPipeList(entry.possible_values).includes(String(depVal))
}

function normalizarOpcionesNumericas(lista) {
  return [...new Set((lista || []).map((v) => parseFloat(v)).filter((n) => !Number.isNaN(n)))]
}

/** Parsea payload GET /public_urls/soc/template. */
export function parseCoppelTemplatePayload(payload) {
  try {
    const attrs = payload.data.attributes || {}
    return {
      conditional_options: attrs.conditional_options || [],
      inputs: attrs.inputs || [],
    }
  } catch (e) {
    return null
  }
}

/**
 * Opciones válidas para un campo según respuestas parciales (producto, política, esquema, etc.).
 * Prioridad: `conditional_options` que coincidan → si ninguna, `inputs[].value_options`.
 */
export function resolveOpcionesCampo(template, fieldKey, answers) {
  if (!template) return []

  if (
    fieldKey === 'tasa_de_interes' &&
    answers.producto_de_financiamiento === 'Mejora de hipoteca'
  ) {
    const mejora = findOpcionesTasaMejoraCoppel(template, answers)
    if (mejora.length) return mejora
  }

  const condBlock = (template.conditional_options || []).find((c) => c.key === fieldKey)
  if (condBlock) {
    for (let i = 0; i < (condBlock.options || []).length; i += 1) {
      const entry = condBlock.options[i]
      if (coincideEntradaCondicional(entry, answers)) {
        const nums = normalizarOpcionesNumericas(entry.options || [])
        if (nums.length) return nums.sort((a, b) => a - b)
      }
    }
  }

  const input = (template.inputs || []).find((i) => i.key === fieldKey)
  if (input) {
    const desdeInputs = []
    ;(input.value_options || []).forEach((entry) => {
      if (coincideValueOption(entry, answers)) {
        desdeInputs.push(...(entry.options || []))
      }
    })
    if (desdeInputs.length) {
      return normalizarOpcionesNumericas(desdeInputs).sort((a, b) => a - b)
    }
  }

  return []
}

/** Enum global de tasas desde inputs (Mercado abierto) o constantes del caller. */
export function enumTasasDesdeTemplate(template, fallbackEnum) {
  const mercado = resolveOpcionesCampo(template, 'tasa_de_interes', {
    tipo_de_financiamiento: 'Mercado abierto',
    producto_de_financiamiento: 'Adquisición de vivienda',
  })
  if (mercado.length) return mercado
  return fallbackEnum || []
}
